/**
 * Kermack–McKendrick SIR model.
 *
 *   dS/dt = -β·S·I / N
 *   dI/dt =  β·S·I / N - γ·I
 *   dR/dt =  γ·I
 *
 * Conservation: S + I + R = N for all t (within numerical tolerance).
 * Basic reproduction number: R₀ = β / γ.
 */

import { integrateRk4 } from './rk4.js';
import type { SirParams, EpiResult, BetaScaler } from './types.js';

export const ENGINE_VERSION = 'biomax-epi-v0.1';

export interface SirOptions {
  /** Optional time-varying β scaler — multiplies β at time t. */
  betaScaler?: BetaScaler;
}

export function simulateSir(params: SirParams, opts: SirOptions = {}): EpiResult {
  const N = params.N;
  const I0 = params.I0;
  const R0Pop = params.R0Pop ?? 0;
  const S0 = N - I0 - R0Pop;
  const dt = params.dtDays ?? 0.5;
  const beta = params.beta;
  const gamma = params.gamma;
  const scaler = opts.betaScaler;
  const t0 = performance.now();

  if (S0 < 0) throw new Error(`SIR: N (${N}) < I0+R0Pop (${I0 + R0Pop})`);
  if (beta < 0) throw new Error(`SIR: beta must be non-negative`);
  if (gamma <= 0) throw new Error(`SIR: gamma must be positive`);

  const f = (t: number, y: number[]): number[] => {
    const S = y[0]!;
    const I = y[1]!;
    const b = scaler ? beta * scaler(t) : beta;
    const newInfections = (b * S * I) / N;
    const recoveries = gamma * I;
    return [-newInfections, newInfections - recoveries, recoveries];
  };

  const integrated = integrateRk4(f, [S0, I0, R0Pop], params.durationDays, dt);

  const compartments: Record<string, number[]> = {
    S: [], I: [], R: [],
  };
  for (const row of integrated.y) {
    compartments.S!.push(row[0]!);
    compartments.I!.push(row[1]!);
    compartments.R!.push(row[2]!);
  }

  const elapsedMs = performance.now() - t0;
  return {
    t: integrated.t,
    compartments,
    receipt: {
      engineVersion: ENGINE_VERSION,
      seed: 'sir',
      elapsedMs,
      steps: integrated.steps,
    },
  };
}

/**
 * Closed-form Kermack-McKendrick reference solution at t=∞.
 *
 * For a closed SIR with S(0)=S0, I(0)=I0, R(0)=0, β, γ, the final
 * recovered fraction R∞ satisfies the implicit equation:
 *
 *   R∞ = N − S0 · exp(−R0 · R∞ / N)
 *
 * where R0 = β/γ. We solve via fixed-point iteration. Used for
 * analytical testing.
 */
export function finalSizeSir(N: number, S0: number, basicR0: number, tol = 1e-10): number {
  let Rinf = N - S0;
  for (let i = 0; i < 200; i++) {
    const next = N - S0 * Math.exp(-basicR0 * Rinf / N);
    if (Math.abs(next - Rinf) < tol) return next;
    Rinf = next;
  }
  return Rinf;
}
